/* ===================================================================
   PostHog product analytics -- OPTIONAL, inert until keyed.

   Mirrors the Sentry pattern in main.tsx: this is a NO-OP unless
   VITE_POSTHOG_KEY is baked into the build (webapp/.env.production).
   Local/dev builds leave it unset, so nothing loads and nothing is sent.

   Bundle discipline: posthog-js (the heavy dependency) is pulled in via a
   DYNAMIC import inside initPosthog(), so it is code-split into its own chunk
   that only downloads when a key is present. An inert build never ships it.

   Privacy: no session recording, no autocaptured passwords (posthog masks
   inputs by default), person profiles only for identified users (we do not
   identify with PII here). Every existing in-app funnel event already flows
   through lib/analytics track() -> the 'rhody:analytics' window bus; we forward
   that bus to PostHog so no funnel call site needs to change.
   =================================================================== */

import type { PostHog } from 'posthog-js';

const DEFAULT_HOST = 'https://us.i.posthog.com';

// Set once init succeeds; every helper below no-ops while this is null.
let client: PostHog | null = null;

/* Initialize PostHog. Safe to call once with the build-time key. Resolves
   quietly (never throws) so a load/init failure can never break app boot. */
export async function initPosthog(key: string, host?: string): Promise<void> {
  if (client || !key) return;
  try {
    const { default: posthog } = await import('posthog-js');
    posthog.init(key, {
      api_host: host || DEFAULT_HOST,
      autocapture: true,
      // SPA pageviews are fired manually on route change (see App.tsx), so the
      // library's own initial-only pageview is disabled to avoid double counts.
      capture_pageview: false,
      capture_pageleave: true,
      person_profiles: 'identified_only',
      disable_session_recording: true,
    });
    client = posthog;

    // Forward the existing analytics bus. Every funnel milestone already emits
    // a 'rhody:analytics' CustomEvent via lib/analytics track(); relay each to
    // PostHog so the whole funnel is captured with zero call-site churn.
    window.addEventListener('rhody:analytics', (e) => {
      const detail = (e as CustomEvent).detail as { event?: string } | undefined;
      if (detail?.event) client?.capture(detail.event, detail);
    });

    // Initial pageview (subsequent ones come from the route-change tracker).
    client.capture('$pageview');
  } catch {
    /* inert on any load/init failure */
  }
}

/* Manual capture (rarely needed -- most events flow through the bus above). */
export function capturePosthogEvent(event: string, props?: Record<string, unknown>): void {
  client?.capture(event, props);
}

/* Fire a SPA pageview on a client-side route change. No-op until ready. */
export function capturePosthogPageview(path?: string): void {
  if (!client) return;
  client.capture('$pageview', path ? { $pathname: path } : undefined);
}

export function posthogReady(): boolean {
  return client !== null;
}
